import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap } from 'rxjs';
import { ProductApiService } from '../product.api.service';
import { ComfortTwinProduct } from '../product.model';
import { ProductService } from '../product.service';

const MIN_SEARCH_LENGTH = 3;

@Component({
	selector: 'app-product-comfort-twin',
	standalone: true,
	imports: [FormsModule, MatCheckboxModule, MatTooltip, NgSelectModule],
	templateUrl: './product-comfort-twin.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductComfortTwinComponent implements OnInit {
	readonly editable = input.required<boolean>();

	readonly enabled = signal(false);
	readonly selectedProductIds = signal<number[]>([]);
	readonly selectedProducts = signal<ComfortTwinProduct[]>([]);
	readonly searchedProducts = signal<ComfortTwinProduct[]>([]);
	readonly productsLoading = signal(false);
	readonly productOptions = computed(() => {
		const currentProductId = Number(this.productService.data.id);
		const options = new Map<number, ComfortTwinProduct>();
		[...this.selectedProducts(), ...this.searchedProducts()]
			.filter((product) => product.id !== currentProductId)
			.forEach((product) => options.set(product.id, product));
		return [...options.values()].map((product) => ({ ...product, label: `${product.code} ${product.name}` }));
	});
	readonly productsSearch$ = new Subject<string>();
	readonly MIN_SEARCH_LENGTH = MIN_SEARCH_LENGTH;

	private readonly productService = inject(ProductService);
	private readonly productApiService = inject(ProductApiService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.syncFromService();

		this.productService.onChangeProductType
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.syncFromService());

		this.productsSearch$
			.pipe(
				filter((term) => !!term && term.length >= MIN_SEARCH_LENGTH),
				debounceTime(500),
				distinctUntilChanged(),
				tap(() => this.productsLoading.set(true)),
				switchMap((term) =>
					this.productApiService
						.getComfortTwinProductsByName(term)
						.pipe(finalize(() => this.productsLoading.set(false))),
				),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((result) => this.searchedProducts.set(result.results));
	}

	toggleEnabled(enabled: boolean): void {
		this.enabled.set(enabled);

		if (!enabled) {
			this.selectedProductIds.set([]);
			this.selectedProducts.set([]);
			this.syncToService();
		}
	}

	selectProducts(productIds: number[]): void {
		const options = this.productOptions();
		this.selectedProductIds.set(productIds ?? []);
		this.selectedProducts.set(options.filter((product) => this.selectedProductIds().includes(product.id)));
		this.syncToService();
	}

	private syncFromService(): void {
		const productIds = this.productService.data.comfortTwinProducts ?? [];

		if (this.isSameSelection(productIds)) {
			return;
		}

		this.selectedProductIds.set(productIds);
		this.enabled.set(productIds.length > 0);

		if (!productIds.length) {
			this.selectedProducts.set([]);
			return;
		}

		this.productApiService
			.getComfortTwinProductsByIds(productIds)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => this.selectedProducts.set(result.results));
	}

	private syncToService(): void {
		this.productService.data.comfortTwinProducts = this.selectedProductIds();
	}

	private isSameSelection(productIds: number[]): boolean {
		const current = this.selectedProductIds();
		return (
			current.length === productIds.length &&
			current.every((id) => productIds.includes(id)) &&
			(current.length === 0 || this.selectedProducts().length > 0)
		);
	}
}
